package org.zq.astrock_ai.data

data class IndexQuote(
    val name: String,
    val price: String,
    val changePercent: String,
    val isUp: Boolean,
)

data class MarketOverview(
    val upCount: Int,
    val downCount: Int,
    val flatCount: Int,
    val turnover: String,
    val northboundFlow: String,
)

data class SectorQuote(
    val name: String,
    val changePercent: String,
    val isUp: Boolean,
    val leadingStock: String,
)

data class RankedStock(
    val rank: Int,
    val name: String,
    val code: String,
    val price: String,
    val changePercent: String,
    val turnover: String,
    val isUp: Boolean,
)

data class WatchlistItem(
    val name: String,
    val code: String,
    val price: String,
    val changePercent: String,
    val isUp: Boolean,
)

data class MetricItem(
    val label: String,
    val value: String,
    val subLabel: String,
    val valueColor: MetricColor = MetricColor.Body,
)

enum class MetricColor { Body, Green, Red, Yellow }

data class NewsItem(
    val tag: String,
    val tagType: NewsTagType,
    val category: String,
    val title: String,
    val summary: String,
    val time: String,
)

enum class NewsTagType { Bullish, Neutral, Bearish }

data class RelatedStock(
    val name: String,
    val code: String,
    val initial: String,
    val avatarColor: Long,
    val price: String,
    val changePercent: String,
    val isUp: Boolean,
)

data class DepthLevel(
    val label: String,
    val price: String,
    val volume: String,
    val barWidth: Float,
    val isAsk: Boolean,
)

data class FactorTag(
    val text: String,
    val type: FactorType,
)

enum class FactorType { Positive, Warning, Negative }

data class ConfidenceItem(
    val label: String,
    val percent: Int,
)

data class StockProfile(
    val name: String,
    val code: String,
    val fullCode: String,
    val initial: String,
    val avatarColor: Long,
    val price: String,
    val changePercent: String,
    val changeAmount: String,
    val isUp: Boolean,
    val industry: String,
    val marketCap: String,
    val openPrice: String,
    val metrics: List<MetricItem>,
    val askLevels: List<DepthLevel>,
    val bidLevels: List<DepthLevel>,
) {
    fun toWatchlistItem() = WatchlistItem(name, code, price, changePercent, isUp)

    val tradePrice: String get() = price.removePrefix("¥").replace(",", "")
}

object MockStockData {
    val indexQuotes = listOf(
        IndexQuote("上证指数", "3,247.89", "-0.82%", false),
        IndexQuote("深证成指", "10,856.32", "+1.24%", true),
        IndexQuote("创业板指", "2,156.78", "+2.15%", true),
        IndexQuote("科创50", "1,023.45", "+1.89%", true),
        IndexQuote("沪深300", "3,876.54", "-0.35%", false),
        IndexQuote("恒生指数", "17,234.56", "+0.67%", true),
        IndexQuote("纳斯达克", "15,678.90", "+1.45%", true),
        IndexQuote("标普500", "4,567.23", "+0.93%", true),
    )

    val marketOverview = MarketOverview(
        upCount = 2846,
        downCount = 1923,
        flatCount = 187,
        turnover = "8,562亿",
        northboundFlow = "+32.6亿",
    )

    val sectorQuotes = listOf(
        SectorQuote("半导体", "+3.28%", true, "中芯国际"),
        SectorQuote("新能源", "+2.86%", true, "宁德时代"),
        SectorQuote("白酒", "+1.92%", true, "贵州茅台"),
        SectorQuote("银行", "+0.45%", true, "招商银行"),
        SectorQuote("光伏", "-1.76%", false, "隆基绿能"),
        SectorQuote("医药", "-0.88%", false, "恒瑞医药"),
        SectorQuote("地产", "-1.24%", false, "万科A"),
        SectorQuote("军工", "+1.15%", true, "中航沈飞"),
    )

    val gainers = listOf(
        RankedStock(1, "比亚迪", "002594", "268.80", "+3.67%", "86.2亿", true),
        RankedStock(2, "山西汾酒", "600809", "218.90", "+2.78%", "12.4亿", true),
        RankedStock(3, "贵州茅台", "600519", "1,688.00", "+2.35%", "48.2亿", true),
        RankedStock(4, "泸州老窖", "000568", "186.45", "+1.23%", "8.6亿", true),
        RankedStock(5, "招商银行", "600036", "34.56", "+1.02%", "14.8亿", true),
    )

    val losers = listOf(
        RankedStock(1, "隆基绿能", "601012", "22.68", "-2.14%", "6.3亿", false),
        RankedStock(2, "洋河股份", "002304", "95.32", "-1.45%", "4.1亿", false),
        RankedStock(3, "宁德时代", "300750", "198.50", "-1.28%", "52.7亿", false),
        RankedStock(4, "五粮液", "000858", "152.18", "-0.56%", "9.8亿", false),
    )

    val turnoverLeaders = listOf(
        RankedStock(1, "宁德时代", "300750", "198.50", "-1.28%", "52.7亿", false),
        RankedStock(2, "贵州茅台", "600519", "1,688.00", "+2.35%", "48.2亿", true),
        RankedStock(3, "比亚迪", "002594", "268.80", "+3.67%", "86.2亿", true),
        RankedStock(4, "招商银行", "600036", "34.56", "+1.02%", "14.8亿", true),
        RankedStock(5, "中芯国际", "688981", "56.32", "+0.95%", "18.5亿", true),
    )

    val metrics = listOf(
        MetricItem("今开", "1,649.20", "昨收 1,649.20"),
        MetricItem("最高", "1,695.00", "振幅 2.78%", MetricColor.Green),
        MetricItem("最低", "1,647.80", "换手率 0.32%", MetricColor.Red),
        MetricItem("成交量", "2.85万手", "成交额 48.2亿"),
        MetricItem("市盈率(TTM)", "28.56", "市净率 8.92"),
        MetricItem("总市值", "2.12万亿", "流通市值 2.12万亿", MetricColor.Yellow),
    )

    val askLevels = listOf(
        DepthLevel("卖5", "1,690.00", "32", 0.40f, true),
        DepthLevel("卖4", "1,689.50", "28", 0.35f, true),
        DepthLevel("卖3", "1,689.00", "45", 0.56f, true),
        DepthLevel("卖2", "1,688.50", "18", 0.22f, true),
        DepthLevel("卖1", "1,688.00", "67", 0.84f, true),
    )

    val bidLevels = listOf(
        DepthLevel("买1", "1,687.80", "124", 1.0f, false),
        DepthLevel("买2", "1,687.50", "56", 0.45f, false),
        DepthLevel("买3", "1,687.00", "89", 0.72f, false),
        DepthLevel("买4", "1,686.50", "34", 0.27f, false),
        DepthLevel("买5", "1,686.00", "72", 0.58f, false),
    )

    val stockProfiles = listOf(
        StockProfile(
            name = "贤丰控股",
            code = "002141",
            fullCode = "002141.SZ",
            initial = "贤",
            avatarColor = 0xFFDC2626,
            price = "¥5.73",
            changePercent = "+9.98%",
            changeAmount = "+0.52",
            isUp = true,
            industry = "电子材料",
            marketCap = "59.19亿",
            openPrice = "5.30",
            metrics = listOf(
                MetricItem("今开", "5.30", "昨收 5.21"),
                MetricItem("最高", "5.73", "振幅 16.51%", MetricColor.Green),
                MetricItem("最低", "4.87", "换手率 17.85%", MetricColor.Red),
                MetricItem("成交量", "184.42万手", "成交额 10.14亿"),
                MetricItem("市盈率(TTM)", "517.74", "市净率 7.33"),
                MetricItem("总市值", "59.19亿", "流通市值 59.19亿", MetricColor.Yellow),
            ),
            askLevels = listOf(
                DepthLevel("卖5", "5.78", "820", 0.35f, true),
                DepthLevel("卖4", "5.77", "640", 0.28f, true),
                DepthLevel("卖3", "5.76", "1,120", 0.48f, true),
                DepthLevel("卖2", "5.75", "980", 0.42f, true),
                DepthLevel("卖1", "5.74", "2,460", 0.90f, true),
            ),
            bidLevels = listOf(
                DepthLevel("买1", "5.73", "5,800", 1.0f, false),
                DepthLevel("买2", "5.72", "2,140", 0.55f, false),
                DepthLevel("买3", "5.70", "1,680", 0.42f, false),
                DepthLevel("买4", "5.68", "920", 0.28f, false),
                DepthLevel("买5", "5.65", "1,350", 0.36f, false),
            ),
        ),
        StockProfile(
            name = "贵州茅台",
            code = "600519",
            fullCode = "600519.SH",
            initial = "茅",
            avatarColor = 0xFFB91C1C,
            price = "¥1,688.00",
            changePercent = "+2.35%",
            changeAmount = "+38.80",
            isUp = true,
            industry = "白酒行业",
            marketCap = "2.12万亿",
            openPrice = "1,649.20",
            metrics = metrics,
            askLevels = askLevels,
            bidLevels = bidLevels,
        ),
        StockProfile(
            name = "宁德时代",
            code = "300750",
            fullCode = "300750.SZ",
            initial = "宁",
            avatarColor = 0xFF059669,
            price = "¥198.50",
            changePercent = "-1.28%",
            changeAmount = "-2.56",
            isUp = false,
            industry = "电池行业",
            marketCap = "8,720亿",
            openPrice = "201.20",
            metrics = listOf(
                MetricItem("今开", "201.20", "昨收 201.06"),
                MetricItem("最高", "202.80", "振幅 2.15%", MetricColor.Green),
                MetricItem("最低", "197.40", "换手率 0.68%", MetricColor.Red),
                MetricItem("成交量", "18.6万手", "成交额 36.8亿"),
                MetricItem("市盈率(TTM)", "22.14", "市净率 4.56"),
                MetricItem("总市值", "8,720亿", "流通市值 7,890亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(198.5f, true),
            bidLevels = depthLevels(198.5f, false),
        ),
        StockProfile(
            name = "比亚迪",
            code = "002594",
            fullCode = "002594.SZ",
            initial = "比",
            avatarColor = 0xFF1D4ED8,
            price = "¥268.80",
            changePercent = "+3.67%",
            changeAmount = "+9.52",
            isUp = true,
            industry = "汽车制造",
            marketCap = "7,830亿",
            openPrice = "259.50",
            metrics = listOf(
                MetricItem("今开", "259.50", "昨收 259.28"),
                MetricItem("最高", "270.20", "振幅 4.12%", MetricColor.Green),
                MetricItem("最低", "258.80", "换手率 1.24%", MetricColor.Red),
                MetricItem("成交量", "32.4万手", "成交额 86.5亿"),
                MetricItem("市盈率(TTM)", "19.86", "市净率 5.12"),
                MetricItem("总市值", "7,830亿", "流通市值 3,120亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(268.8f, true),
            bidLevels = depthLevels(268.8f, false),
        ),
        StockProfile(
            name = "中芯国际",
            code = "688981",
            fullCode = "688981.SH",
            initial = "芯",
            avatarColor = 0xFF7C3AED,
            price = "¥56.32",
            changePercent = "+0.95%",
            changeAmount = "+0.53",
            isUp = true,
            industry = "半导体",
            marketCap = "4,450亿",
            openPrice = "55.68",
            metrics = listOf(
                MetricItem("今开", "55.68", "昨收 55.79"),
                MetricItem("最高", "56.88", "振幅 2.38%", MetricColor.Green),
                MetricItem("最低", "55.42", "换手率 0.45%", MetricColor.Red),
                MetricItem("成交量", "28.2万手", "成交额 15.8亿"),
                MetricItem("市盈率(TTM)", "48.32", "市净率 3.86"),
                MetricItem("总市值", "4,450亿", "流通市值 2,210亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(56.32f, true),
            bidLevels = depthLevels(56.32f, false),
        ),
        StockProfile(
            name = "五粮液",
            code = "000858",
            fullCode = "000858.SZ",
            initial = "五",
            avatarColor = 0xFF2563EB,
            price = "¥152.18",
            changePercent = "-0.56%",
            changeAmount = "-0.86",
            isUp = false,
            industry = "白酒行业",
            marketCap = "5,890亿",
            openPrice = "153.20",
            metrics = listOf(
                MetricItem("今开", "153.20", "昨收 153.04"),
                MetricItem("最高", "153.80", "振幅 1.42%", MetricColor.Green),
                MetricItem("最低", "151.60", "换手率 0.28%", MetricColor.Red),
                MetricItem("成交量", "8.6万手", "成交额 13.1亿"),
                MetricItem("市盈率(TTM)", "18.56", "市净率 4.12"),
                MetricItem("总市值", "5,890亿", "流通市值 5,890亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(152.18f, true),
            bidLevels = depthLevels(152.18f, false),
        ),
        StockProfile(
            name = "隆基绿能",
            code = "601012",
            fullCode = "601012.SH",
            initial = "隆",
            avatarColor = 0xFF0D9488,
            price = "¥22.68",
            changePercent = "-2.14%",
            changeAmount = "-0.49",
            isUp = false,
            industry = "光伏设备",
            marketCap = "1,720亿",
            openPrice = "23.12",
            metrics = listOf(
                MetricItem("今开", "23.12", "昨收 23.17"),
                MetricItem("最高", "23.28", "振幅 3.05%", MetricColor.Green),
                MetricItem("最低", "22.52", "换手率 1.86%", MetricColor.Red),
                MetricItem("成交量", "68.4万手", "成交额 15.6亿"),
                MetricItem("市盈率(TTM)", "14.22", "市净率 2.18"),
                MetricItem("总市值", "1,720亿", "流通市值 1,720亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(22.68f, true),
            bidLevels = depthLevels(22.68f, false),
        ),
        StockProfile(
            name = "招商银行",
            code = "600036",
            fullCode = "600036.SH",
            initial = "招",
            avatarColor = 0xFFDC2626,
            price = "¥34.56",
            changePercent = "+1.02%",
            changeAmount = "+0.35",
            isUp = true,
            industry = "银行",
            marketCap = "8,720亿",
            openPrice = "34.18",
            metrics = listOf(
                MetricItem("今开", "34.18", "昨收 34.21"),
                MetricItem("最高", "34.72", "振幅 1.58%", MetricColor.Green),
                MetricItem("最低", "34.12", "换手率 0.22%", MetricColor.Red),
                MetricItem("成交量", "42.8万手", "成交额 14.8亿"),
                MetricItem("市盈率(TTM)", "5.86", "市净率 0.92"),
                MetricItem("总市值", "8,720亿", "流通市值 7,120亿", MetricColor.Yellow),
            ),
            askLevels = depthLevels(34.56f, true),
            bidLevels = depthLevels(34.56f, false),
        ),
        StockProfile(
            name = "山西汾酒",
            code = "600809",
            fullCode = "600809.SH",
            initial = "汾",
            avatarColor = 0xFF059669,
            price = "¥218.90",
            changePercent = "+2.78%",
            changeAmount = "+5.92",
            isUp = true,
            industry = "白酒行业",
            marketCap = "2,680亿",
            openPrice = "213.20",
            metrics = defaultMetrics("213.20", "218.90", "212.50", true),
            askLevels = depthLevels(218.9f, true),
            bidLevels = depthLevels(218.9f, false),
        ),
        StockProfile(
            name = "泸州老窖",
            code = "000568",
            fullCode = "000568.SZ",
            initial = "泸",
            avatarColor = 0xFFD97706,
            price = "¥186.45",
            changePercent = "+1.23%",
            changeAmount = "+2.28",
            isUp = true,
            industry = "白酒行业",
            marketCap = "2,720亿",
            openPrice = "184.20",
            metrics = defaultMetrics("184.20", "186.45", "183.80", true),
            askLevels = depthLevels(186.45f, true),
            bidLevels = depthLevels(186.45f, false),
        ),
        StockProfile(
            name = "洋河股份",
            code = "002304",
            fullCode = "002304.SZ",
            initial = "洋",
            avatarColor = 0xFF9333EA,
            price = "¥95.32",
            changePercent = "-1.45%",
            changeAmount = "-1.38",
            isUp = false,
            industry = "白酒行业",
            marketCap = "1,430亿",
            openPrice = "96.80",
            metrics = defaultMetrics("96.80", "95.32", "94.90", false),
            askLevels = depthLevels(95.32f, true),
            bidLevels = depthLevels(95.32f, false),
        ),
        StockProfile(
            name = "恒瑞医药",
            code = "600276",
            fullCode = "600276.SH",
            initial = "恒",
            avatarColor = 0xFF0EA5E9,
            price = "¥48.56",
            changePercent = "-0.88%",
            changeAmount = "-0.43",
            isUp = false,
            industry = "医药",
            marketCap = "3,120亿",
            openPrice = "49.10",
            metrics = defaultMetrics("49.10", "48.56", "48.20", false),
            askLevels = depthLevels(48.56f, true),
            bidLevels = depthLevels(48.56f, false),
        ),
        StockProfile(
            name = "万科A",
            code = "000002",
            fullCode = "000002.SZ",
            initial = "万",
            avatarColor = 0xFF64748B,
            price = "¥7.82",
            changePercent = "-1.24%",
            changeAmount = "-0.10",
            isUp = false,
            industry = "地产",
            marketCap = "936亿",
            openPrice = "7.92",
            metrics = defaultMetrics("7.92", "7.82", "7.78", false),
            askLevels = depthLevels(7.82f, true),
            bidLevels = depthLevels(7.82f, false),
        ),
        StockProfile(
            name = "中航沈飞",
            code = "600893",
            fullCode = "600893.SH",
            initial = "沈",
            avatarColor = 0xFF4F46E5,
            price = "¥42.18",
            changePercent = "+1.15%",
            changeAmount = "+0.48",
            isUp = true,
            industry = "军工",
            marketCap = "1,150亿",
            openPrice = "41.60",
            metrics = defaultMetrics("41.60", "42.18", "41.40", true),
            askLevels = depthLevels(42.18f, true),
            bidLevels = depthLevels(42.18f, false),
        ),
    )

    val defaultWatchlistCodes = listOf(
        "002141", "600519", "300750", "002594", "688981", "000858", "601012", "600036",
    )

    val watchlist = stockProfiles.map { it.toWatchlistItem() }

    fun profileByCode(code: String): StockProfile? = stockProfiles.find { it.code == code }

    fun indexOfStock(code: String): Int = stockProfiles.indexOfFirst { it.code == code }

    fun addableStocks(existingCodes: Set<String>): List<StockProfile> =
        stockProfiles.filter { it.code !in existingCodes }

    fun newsForStock(stock: StockProfile): List<NewsItem> = listOf(
        NewsItem(
            tag = if (stock.isUp) "利好" else "利空",
            tagType = if (stock.isUp) NewsTagType.Bullish else NewsTagType.Bearish,
            category = "公司公告",
            title = "${stock.name}：最新业绩披露，${stock.industry}板块关注度提升",
            summary = "${stock.name}（${stock.fullCode}）今日收报 ${stock.price}，${stock.changePercent}，市场持续关注后续走势...",
            time = "10-25 19:32",
        ),
        NewsItem(
            tag = "中性",
            tagType = NewsTagType.Neutral,
            category = "券商研报",
            title = "机构观点：维持${stock.name}「增持」评级",
            summary = "分析师认为${stock.name}估值处于合理区间，建议关注${stock.industry}行业政策变化...",
            time = "10-24 08:15",
        ),
        NewsItem(
            tag = "中性",
            tagType = NewsTagType.Neutral,
            category = "行业新闻",
            title = "${stock.industry}板块${if (stock.isUp) "走强" else "震荡"}，${stock.name}成交活跃",
            summary = "板块内多只个股跟随大盘波动，${stock.name}成交额位居前列...",
            time = "10-23 14:28",
        ),
    )

    fun predictionTarget(stock: StockProfile): String {
        val base = stock.tradePrice.replace(",", "").toFloatOrNull() ?: 100f
        return formatPrice(base * 1.04f)
    }

    fun predictionStop(stock: StockProfile): String {
        val base = stock.tradePrice.replace(",", "").toFloatOrNull() ?: 100f
        return formatPrice(base * 0.95f)
    }

    fun predictionUpside(stock: StockProfile): String {
        val pct = if (stock.isUp) "+4.03%" else "+3.85%"
        return "$pct 上涨空间"
    }

    fun predictionRisk(stock: StockProfile): String = "-5.00% 风险线"

    val volumeBars = listOf(
        0.45f, 0.62f, 0.38f, 0.78f, 0.55f, 0.82f, 0.67f, 0.43f, 0.91f, 0.73f,
        0.58f, 0.85f, 0.69f, 0.47f, 0.95f, 0.88f, 0.76f, 0.65f, 0.72f, 0.58f,
    )

    val factorTags = listOf(
        FactorTag("技术面突破", FactorType.Positive),
        FactorTag("资金流入", FactorType.Positive),
        FactorTag("业绩超预期", FactorType.Positive),
        FactorTag("大盘波动", FactorType.Warning),
        FactorTag("估值偏高", FactorType.Negative),
    )

    val confidenceItems = listOf(
        ConfidenceItem("技术面", 85),
        ConfidenceItem("基本面", 72),
        ConfidenceItem("资金面", 68),
        ConfidenceItem("情绪面", 89),
    )

    val newsItems = listOf(
        NewsItem("利好", NewsTagType.Bullish, "公司公告",
            "贵州茅台2024年三季报：营收同比增长17.2%，净利润突破500亿元大关",
            "公司第三季度实现营收336.92亿元，同比增长17.04%；归属于上市公司股东的净利润168.96亿元，同比增长15.68%...",
            "10-25 19:32"),
        NewsItem("中性", NewsTagType.Neutral, "券商研报",
            "中信证券：维持贵州茅台\"买入\"评级，目标价上调至1,850元",
            "考虑到公司高端产品占比提升及直营渠道改革成效显现，我们上调2024-2026年盈利预测...",
            "10-24 08:15"),
        NewsItem("利好", NewsTagType.Bullish, "行业新闻",
            "白酒行业Q3数据出炉：高端白酒景气度持续，茅台市占率进一步提升",
            "据行业协会数据，2024年第三季度高端白酒销售额同比增长12.3%，其中飞天茅台销售额占比达38.6%...",
            "10-23 14:28"),
        NewsItem("利空", NewsTagType.Bearish, "市场动态",
            "北向资金连续三日净卖出贵州茅台，累计减持金额超15亿元",
            "港交所数据显示，北向资金今日净卖出贵州茅台5.23亿元，为连续第三个交易日净卖出...",
            "10-22 16:05"),
        NewsItem("中性", NewsTagType.Neutral, "公司公告",
            "贵州茅台：拟实施2024年中期分红，每10股派发现金红利238.82元",
            "公司董事会审议通过2024年中期利润分配方案，合计拟派发现金红利300.01亿元...",
            "10-21 20:00"),
    )

    val relatedStocks = listOf(
        RelatedStock("五粮液", "000858", "五", 0xFF2563EB, "152.18", "-0.56%", false),
        RelatedStock("泸州老窖", "000568", "泸", 0xFFD97706, "186.45", "+1.23%", true),
        RelatedStock("山西汾酒", "600809", "汾", 0xFF059669, "218.90", "+2.78%", true),
        RelatedStock("洋河股份", "002304", "洋", 0xFF9333EA, "95.32", "-1.45%", false),
    )

    fun generateChartData(seed: Int = 0): Pair<List<Float>, List<Float>> {
        val random = kotlin.random.Random(seed)
        val history = mutableListOf<Float>()
        var base = 1620f + seed * 12f
        repeat(60) {
            base += ((random.nextFloat() - 0.48f) * 25f)
            history.add(base)
        }
        val last = history.last()
        val prediction = (1..10).map { i ->
            last + i * 6.8f + random.nextFloat() * 4f
        }
        return history to prediction
    }

    private fun defaultMetrics(
        open: String,
        high: String,
        low: String,
        isUp: Boolean,
    ): List<MetricItem> = listOf(
        MetricItem("今开", open, "昨收 $open"),
        MetricItem("最高", high, "振幅 2.10%", if (isUp) MetricColor.Green else MetricColor.Body),
        MetricItem("最低", low, "换手率 0.45%", MetricColor.Red),
        MetricItem("成交量", "12.6万手", "成交额 18.5亿"),
        MetricItem("市盈率(TTM)", "24.50", "市净率 3.20"),
        MetricItem("总市值", "1,200亿", "流通市值 1,100亿", MetricColor.Yellow),
    )

    private fun formatPrice(value: Float): String {
        val scaled = kotlin.math.round(value * 100).toInt()
        val whole = scaled / 100
        val frac = scaled % 100
        val wholeStr = whole.toString().reversed().chunked(3).joinToString(",").reversed()
        return "$wholeStr.${frac.toString().padStart(2, '0')}"
    }

    private fun depthLevels(base: Float, asks: Boolean): List<DepthLevel> {
        if (asks) {
            return (5 downTo 1).map { level ->
                val offset = level * 0.5f
                DepthLevel(
                    label = "卖$level",
                    price = formatPrice(base + offset),
                    volume = "${18 + level * 7}",
                    barWidth = 0.2f + level * 0.12f,
                    isAsk = true,
                )
            }
        }
        return (1..5).map { level ->
            val offset = level * 0.5f
            DepthLevel(
                label = "买$level",
                price = formatPrice(base - offset),
                volume = "${24 + level * 11}",
                barWidth = minOf(1f, 0.35f + level * 0.13f),
                isAsk = false,
            )
        }
    }
}
